/*
  Miamon — slider del hero.
  Crossfade entre diapositivas con dots de progreso, replicando el
  comportamiento del componente `mm-xfade` de la web en WordPress:
  cada imagen aguanta unos segundos y cruza con la siguiente.

  Custom element para que conviva con el editor de temas, que reinyecta
  secciones sin recargar la página.
*/
if (!customElements.get('mm-hero')) {
  customElements.define(
    'mm-hero',
    class MmHero extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.mm-hero__slide'));
        this.dots = Array.from(this.querySelectorAll('.mm-hero__dot'));
        if (this.slides.length < 2) return;

        this.index = 0;
        this.hold = Number(this.dataset.hold) || 9000;
        this.timer = null;
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.goTo(i));
        });

        this.start();

        // Sin sentido rotar imágenes que no están en pantalla.
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              entries.forEach((e) => (e.isIntersecting ? this.start() : this.stop()));
            },
            { threshold: 0.15 }
          );
          this.io.observe(this);
        }

        // Al volver a la pestaña, reiniciar el ciclo en vez de saltar de golpe.
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      disconnectedCallback() {
        this.stop();
        if (this.resetTimer) clearTimeout(this.resetTimer);
        if (this.io) this.io.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      onVisibility = () => {
        document.hidden ? this.stop() : this.start();
      };

      goTo(i) {
        this.index = ((i % this.slides.length) + this.slides.length) % this.slides.length;
        this.render();
        this.start();
      }

      next() {
        this.goTo(this.index + 1);
      }

      render() {
        this.slides.forEach((slide, i) => {
          var activo = i === this.index;
          slide.dataset.active = activo ? 'true' : 'false';
          var link = slide.querySelector('.mm-hero__slide-link');
          if (link) link.tabIndex = activo ? 0 : -1;
        });

        this.dots.forEach((dot, i) => {
          var activo = i === this.index;
          dot.setAttribute('aria-current', activo ? 'true' : 'false');
          var fill = dot.querySelector('span');
          if (!fill) return;
          // Reiniciar la barra: sin esto la animación no vuelve a correr
          // cuando el dot ya estaba activo.
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        });
      }

      start() {
        if (this.reduced) return;
        this.stop();
        this.timer = setInterval(this.next.bind(this), this.hold);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}

/*
  Carrusel pequeño de producto del hero — coverflow de 3 visibles.

  Mismo patrón que `mm-reels`: el JS no mueve nada por su cuenta, solo
  escribe en cada item un `data-pos` (0 = centro, ±1 = laterales,
  ±2 = fuera de plano) y assets/section-mm-hero.css resuelve posición,
  escala, brillo y recorte.

  Va dentro de una diapositiva, así que se funde con ella por CSS. Lo
  único que hace falta aquí es rotar el producto destacado y parar el
  ciclo mientras la diapositiva no está a la vista, para que al volver
  arranque limpio en vez de aparecer a mitad de recorrido.
*/
if (!customElements.get('mm-hero-rail')) {
  customElements.define(
    'mm-hero-rail',
    class MmHeroRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.mm-hero__rail-track');
        this.items = Array.from(this.querySelectorAll('.mm-hero__rail-item'));
        if (!this.track || this.items.length === 0) return;

        this.count = this.items.length;
        this.index = 0;
        this.timer = null;
        this.resetTimer = null;
        this.activa = null; // aún sin determinar; lo fija el primer sync()
        this.gen = 0; // invalida los reposicionamientos diferidos ya obsoletos
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        var host = this.closest('mm-hero');
        this.delay = host ? Number(host.dataset.railDelay) || 3000 : 3000;

        this.items.forEach((item, i) => {
          // Un lateral no navega al hacer clic: primero se centra.
          // Centrar al pasar el ratón se probó y se descartó: al recolocarse
          // las tarjetas, la que quedaba bajo el cursor disparaba su propio
          // mouseenter y el carrusel se desbocaba en cadena.
          item.addEventListener('click', (event) => {
            if (item.dataset.pos === '0') return;
            event.preventDefault();
            this.goTo(i);
          });
        });

        // El puntero encima congela el carrusel: si no, el producto que ibas
        // a pulsar se va de debajo del cursor a los pocos segundos.
        // Se usan pointerover/pointerout porque sí burbujean: el contenedor
        // tiene pointer-events:none y solo los items reciben el puntero, así
        // que pointerenter/leave no llegaban de forma fiable.
        this.addEventListener('pointerover', (event) => {
          if (event.pointerType === 'touch') return;
          this.stop();
        });
        this.addEventListener('pointerout', (event) => {
          if (event.pointerType === 'touch') return;
          // Moverse de una tarjeta a otra no es salir del carrusel.
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          if (this.activa !== false) this.start();
        });

        // Arrastre, con el mismo gesto que reels y testimonios
        // (assets/mm-drag.js). Va en la PISTA, no en el contenedor: el
        // contenedor tiene `pointer-events: none` y colgarlo ahí solo
        // funcionaba si el gesto empezaba justo encima de un bote — entre
        // bote y bote no pasaba nada.
        if (window.mmArrastre) {
          window.mmArrastre(this.track, (dir) => {
            dir > 0 ? this.next() : this.prev();
          });
        }

        this.slide = this.closest('.mm-hero__slide');
        if (this.slide) {
          // La diapositiva marca su estado en data-active; seguimos ese dato
          // en vez de duplicar el temporizador del hero.
          this.mo = new MutationObserver(() => this.sync());
          this.mo.observe(this.slide, { attributes: true, attributeFilter: ['data-active'] });
        }

        this.render(true);
        this.sync();
      }

      disconnectedCallback() {
        this.stop();
        this.gen++;
        if (this.mo) this.mo.disconnect();
      }

      sync() {
        var activa = !this.slide || this.slide.dataset.active === 'true';

        // MmHero.render() reescribe data-active en TODAS las diapositivas en
        // cada tick, y setAttribute dispara el observer aunque el valor no
        // cambie. Sin este corte, cada tick reiniciaba el temporizador del
        // carrusel y con rail_delay > hold no avanzaba nunca.
        if (activa === this.activa) return;
        this.activa = activa;

        if (this.resetTimer) {
          clearTimeout(this.resetTimer);
          this.resetTimer = null;
        }

        if (activa) {
          this.start();
          return;
        }

        this.stop();

        // El reset no puede hacerse ya: la diapositiva tarda --mm-hero-fade
        // en apagarse y el salto se vería a plena opacidad.
        var host = this.closest('mm-hero');
        var fade = host
          ? parseFloat(getComputedStyle(host).getPropertyValue('--mm-hero-fade'))
          : 0;
        if (!fade) fade = 1200;

        this.resetTimer = setTimeout(() => {
          this.resetTimer = null;
          this.index = 0;
          this.render(true);
        }, fade + 80);
      }

      /* Distancia circular con signo entre el índice i y el actual, para que
         el salto entre el último y el primero sea corto. */
      offsetOf(i) {
        var half = Math.floor(this.count / 2);
        var diff = (((i - this.index) % this.count) + this.count) % this.count;
        return diff > half ? diff - this.count : diff;
      }

      goTo(i) {
        this.index = ((i % this.count) + this.count) % this.count;
        this.render(false);
        if (this.timer) this.start();
      }

      next() {
        this.goTo(this.index + 1);
      }

      prev() {
        this.goTo(this.index - 1);
      }

      /* Escribe una posición. Con `instantaneo` se apaga la transición para
         ese cambio: sirve para recolocar sin que se vea el viaje. */
      colocar(item, pos, instantaneo) {
        if (instantaneo) item.style.transition = 'none';
        item.dataset.pos = String(pos);
        item.dataset.active = pos === 0 ? 'true' : 'false';
        var link = item.querySelector('a');
        if (link) link.tabIndex = pos === 0 ? 0 : -1;
        // opacity:0 no saca del árbol de accesibilidad: sin esto el lector
        // de pantalla anunciaba también los productos invisibles.
        item.setAttribute('aria-hidden', Math.abs(pos) === 2 ? 'true' : 'false');
        if (instantaneo) {
          void item.offsetWidth; // fuerza el reflujo antes de devolver la transición
          item.style.transition = '';
        }
      }

      render(instantaneo) {
        var gen = ++this.gen;

        this.items.forEach((item, i) => {
          // Todo lo que pase de ±1 se aparca en el hueco oculto de su lado:
          // el CSS lo saca del recorte y lo deja a opacidad 0.
          var pos = Math.max(-2, Math.min(2, this.offsetOf(i)));
          var previa = item.dataset.pos ? Number(item.dataset.pos) : null;

          if (instantaneo || previa === null) {
            this.colocar(item, pos, true);
            return;
          }

          // Un movimiento de un solo hueco es el caso normal: se anima.
          if (Math.abs(pos - previa) <= 1) {
            this.colocar(item, pos, false);
            return;
          }

          // A partir de aquí el ciclo ha dado la vuelta. Animar el salto tal
          // cual haría que la tarjeta cruzara el escenario entero a la vista.
          if (Math.abs(previa) <= 1 && Math.abs(pos) === 2) {
            // Estaba a la vista y le toca desaparecer: que salga por SU lado
            // en vez de irse al hueco contrario. Los dos huecos ocultos son
            // equivalentes, así que basta con quedarse en el de al lado.
            this.colocar(item, previa < 0 ? -2 : 2, false);
            return;
          }

          // Viene de fuera de plano. Se le pone de golpe en el hueco oculto
          // del lado por el que debe entrar (invisible, nadie ve el salto) y
          // al frame siguiente se anima hasta su sitio.
          var hueco = pos < 0 ? -2 : 2;
          this.colocar(item, pos === 0 ? pos : hueco, true);
          if (pos === 0 || Math.abs(pos) === 2) return;

          requestAnimationFrame(() => {
            if (gen !== this.gen) return; // ya hubo otro render por medio
            this.colocar(item, pos, false);
          });
        });
      }

      start() {
        if (this.reduced || this.count < 2) return;
        this.stop();
        this.timer = setInterval(this.next.bind(this), this.delay);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}
